/**
 * Money — la ÚNICA representación de dinero del ERP MG (Invariante 1, ADR-0001).
 *
 * - Internamente usa decimal.js con 40 dígitos de precisión y redondeo half-up.
 * - Es inmutable: toda operación devuelve un Money nuevo.
 * - NO expone `number`: no existe toNumber(). Si necesitas mostrar, usa toDisplay();
 *   si necesitas persistir, toDB() (8 decimales, numeric(24,8)); si necesitas el total
 *   de un documento, toDocumento() (2 decimales half-up).
 * - Redondear a 2 decimales SOLO ocurre en toDocumento()/toDisplay()/redondear2().
 *   Las operaciones intermedias (líneas, prorrateos, conversiones) conservan precisión.
 *
 * decimal.js se importa ÚNICAMENTE aquí (dependency-cruiser lo verifica).
 */
import { Decimal } from 'decimal.js';

const Dec = Decimal.clone({
  precision: 40,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -30,
  toExpPos: 40,
});

/** Entradas aceptadas. El canónico es string decimal con punto ("1234.56"). */
export type MoneyInput = Money | string | number | bigint;

const RE_DECIMAL = /^[+-]?(\d+)(\.\d+)?$/;

export class MoneyError extends Error {
  override readonly name = 'MoneyError';
}

/** Normaliza cualquier entrada a Decimal, rechazando NaN/Infinity/strings no decimales. */
function aDecimal(v: MoneyInput, contexto = 'valor'): InstanceType<typeof Dec> {
  if (v instanceof Money) return v.decimal;
  if (typeof v === 'bigint') return new Dec(v.toString());
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) throw new MoneyError(`Money: ${contexto} no es un número finito`);
    // new Decimal(number) usa la representación decimal más corta del double (0.1 → "0.1"),
    // así que no arrastra el error binario. Aun así, el canónico es string.
    return new Dec(v);
  }
  if (typeof v === 'string') {
    const s = v.trim();
    if (!RE_DECIMAL.test(s)) throw new MoneyError(`Money: "${v}" no es un decimal válido (usa "1234.56")`);
    return new Dec(s);
  }
  throw new MoneyError(`Money: tipo de ${contexto} no soportado`);
}

export class Money {
  /** @internal */
  readonly decimal: InstanceType<typeof Dec>;

  private constructor(d: InstanceType<typeof Dec>) {
    this.decimal = d;
    Object.freeze(this);
  }

  // ─── Construcción ──────────────────────────────────────────────────────────
  static parse(v: MoneyInput): Money {
    return new Money(aDecimal(v));
  }

  static cero(): Money {
    return new Money(new Dec(0));
  }

  /** Desde columna numeric de Postgres (string) — null se interpreta como 0. */
  static desdeDB(v: string | null | undefined): Money {
    if (v === null || v === undefined || v === '') return Money.cero();
    return Money.parse(v);
  }

  /** Suma de varios montos (vacío → 0). */
  static sumar(items: Iterable<Money>): Money {
    let acc = new Dec(0);
    for (const m of items) acc = acc.plus(m.decimal);
    return new Money(acc);
  }

  static esMoney(v: unknown): v is Money {
    return v instanceof Money;
  }

  // ─── Aritmética ────────────────────────────────────────────────────────────
  add(o: MoneyInput): Money {
    return new Money(this.decimal.plus(aDecimal(o, 'sumando')));
  }

  sub(o: MoneyInput): Money {
    return new Money(this.decimal.minus(aDecimal(o, 'sustraendo')));
  }

  mul(f: MoneyInput): Money {
    return new Money(this.decimal.times(aDecimal(f, 'factor')));
  }

  div(f: MoneyInput): Money {
    const d = aDecimal(f, 'divisor');
    if (d.isZero()) throw new MoneyError('Money: división por cero');
    return new Money(this.decimal.div(d));
  }

  neg(): Money {
    return new Money(this.decimal.neg());
  }

  abs(): Money {
    return new Money(this.decimal.abs());
  }

  /** this × pct / 100 (ej. IVA 16 → porcentaje("16")). */
  porcentaje(pct: MoneyInput): Money {
    return new Money(this.decimal.times(aDecimal(pct, 'porcentaje')).div(100));
  }

  // ─── Comparación ───────────────────────────────────────────────────────────
  cmp(o: MoneyInput): -1 | 0 | 1 {
    return this.decimal.cmp(aDecimal(o)) as -1 | 0 | 1;
  }

  eq(o: MoneyInput): boolean {
    return this.cmp(o) === 0;
  }

  gt(o: MoneyInput): boolean {
    return this.cmp(o) > 0;
  }

  gte(o: MoneyInput): boolean {
    return this.cmp(o) >= 0;
  }

  lt(o: MoneyInput): boolean {
    return this.cmp(o) < 0;
  }

  lte(o: MoneyInput): boolean {
    return this.cmp(o) <= 0;
  }

  esCero(): boolean {
    return this.decimal.isZero();
  }

  esNegativo(): boolean {
    return this.decimal.isNegative() && !this.decimal.isZero();
  }

  esPositivo(): boolean {
    return this.decimal.isPositive() && !this.decimal.isZero();
  }

  min(o: Money): Money {
    return this.lte(o) ? this : o;
  }

  max(o: Money): Money {
    return this.gte(o) ? this : o;
  }

  // ─── Conversión bimoneda (tasas SIEMPRE explícitas — ADR-0002) ─────────────
  /** $ → Bs con la tasa dada (la del documento, jamás "la del día" implícita). */
  usdABs(tasa: MoneyInput): Money {
    const t = aDecimal(tasa, 'tasa');
    if (t.lte(0)) throw new MoneyError('Money: la tasa debe ser positiva');
    return new Money(this.decimal.times(t));
  }

  /** Bs → $ con la tasa dada. */
  bsAUsd(tasa: MoneyInput): Money {
    const t = aDecimal(tasa, 'tasa');
    if (t.lte(0)) throw new MoneyError('Money: la tasa debe ser positiva');
    return new Money(this.decimal.div(t));
  }

  /** $ reales × factor (paralela ÷ bcv) = "$ a BCV" referencial. Los cobros usan factor 1. */
  aplicarFactor(factor: MoneyInput): Money {
    const f = aDecimal(factor, 'factor divisa');
    if (f.lte(0)) throw new MoneyError('Money: el factor divisa debe ser positivo');
    return new Money(this.decimal.times(f));
  }

  /** "$ a BCV" ÷ factor = $ reales. Los cobros usan factor 1. */
  quitarFactor(factor: MoneyInput): Money {
    const f = aDecimal(factor, 'factor divisa');
    if (f.lte(0)) throw new MoneyError('Money: el factor divisa debe ser positivo');
    return new Money(this.decimal.div(f));
  }

  // ─── Reparto proporcional sin perder centavos ──────────────────────────────
  /**
   * Distribuye este monto (redondeado a 2 decimales) según pesos, garantizando que la
   * suma de las partes es EXACTAMENTE el total (método del mayor residuo). Se usa para
   * prorratear gastos en compras, descuentos globales entre líneas, etc.
   */
  allocate(pesos: MoneyInput[]): Money[] {
    if (pesos.length === 0) throw new MoneyError('Money.allocate: sin pesos');
    const ws = pesos.map((p) => aDecimal(p, 'peso'));
    if (ws.some((w) => w.isNegative())) throw new MoneyError('Money.allocate: peso negativo');
    const sumaPesos = ws.reduce((a, w) => a.plus(w), new Dec(0));
    if (sumaPesos.isZero()) throw new MoneyError('Money.allocate: la suma de pesos es cero');

    const total = this.decimal.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    const signo = total.isNegative() ? -1 : 1;
    const totalCent = total.abs().times(100); // entero exacto de centavos

    const crudos = ws.map((w) => totalCent.times(w).div(sumaPesos));
    const bases = crudos.map((c) => c.floor());
    let resto = totalCent.minus(bases.reduce((a, b) => a.plus(b), new Dec(0))).toNumber();

    // Orden por residuo descendente; empate → índice menor primero (determinista)
    const orden = crudos
      .map((c, i) => ({ i, residuo: c.minus(bases[i] as InstanceType<typeof Dec>) }))
      .sort((a, b) => b.residuo.cmp(a.residuo) || a.i - b.i);

    const resultado = bases.slice();
    for (const { i } of orden) {
      if (resto <= 0) break;
      resultado[i] = (resultado[i] as InstanceType<typeof Dec>).plus(1);
      resto -= 1;
    }
    return resultado.map((c) => new Money(c.div(100).times(signo)));
  }

  // ─── Redondeo y salida (los ÚNICOS puntos donde se redondea a 2 decimales) ─
  /**
   * La precisión "real" de un monto es la de la DB: 8 decimales (numeric(24,8)). Los 40
   * dígitos internos existen solo para que las divisiones (Bs→$, prorrateos) no pierdan
   * nada. Por eso, antes de llevar a 2 decimales se normaliza a 8: así 2.674999999999…9
   * (artefacto de una división) se trata como 2.675 → 2.68, igual que lo haría Postgres
   * al leer la columna. Resultado: memoria y DB redondean SIEMPRE igual.
   */
  private a8(): InstanceType<typeof Dec> {
    return this.decimal.toDecimalPlaces(8, Decimal.ROUND_HALF_UP);
  }

  /**
   * Hacia arriba al múltiplo de `paso` (cantidades de combos y recetas, spec 20: 1,3 → 2 con paso 1; 12,341 → 12,35 con
   * paso 0,01). Antes se lleva a 8 decimales, como se guarda, para que un 3,000…001 de una división no suba a 4.
   */
  techoMultiplo(paso: MoneyInput): Money {
    const p = aDecimal(paso, 'paso');
    if (!p.gt(0)) throw new MoneyError('Money: el paso debe ser mayor que cero');
    return new Money(this.a8().div(p).ceil().mul(p));
  }

  /** Hacia abajo al múltiplo de `paso` (cuántos empaques enteros caben en una necesidad, spec 20). */
  pisoMultiplo(paso: MoneyInput): Money {
    const p = aDecimal(paso, 'paso');
    if (!p.gt(0)) throw new MoneyError('Money: el paso debe ser mayor que cero');
    return new Money(this.a8().div(p).floor().mul(p));
  }

  /** Half-up a 2 decimales. Solo para totales de documento / montos a cobrar. */
  redondear2(): Money {
    return new Money(this.a8().toDecimalPlaces(2, Decimal.ROUND_HALF_UP));
  }

  /** Canónico para numeric(24,8): 8 decimales fijos, punto decimal, sin exponente. */
  toDB(): string {
    return this.decimal.toFixed(8, Decimal.ROUND_HALF_UP);
  }

  /** Canónico de documento: 2 decimales half-up ("1234.56"). */
  toDocumento(): string {
    return this.a8().toFixed(2, Decimal.ROUND_HALF_UP);
  }

  /** Presentación es-VE: "1.234,56" (miles con punto, decimales con coma, 2 decimales). */
  toDisplay(): string {
    const fijo = this.a8().toFixed(2, Decimal.ROUND_HALF_UP);
    const negativo = fijo.startsWith('-');
    const [ent = '0', dec = '00'] = (negativo ? fijo.slice(1) : fijo).split('.');
    const miles = ent.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${negativo ? '-' : ''}${miles},${dec}`;
  }

  /** Canónico sin redondear (precisión completa). Úsalo solo para logs/JSON. */
  toString(): string {
    return this.decimal.toFixed();
  }

  toJSON(): string {
    return this.toString();
  }

  /** Bloquea coerción numérica accidental (`money + 1`, `money * 2`). */
  valueOf(): never {
    throw new MoneyError('Money no se puede usar en aritmética nativa: usa add/sub/mul/div');
  }
}
